import React from "react";
import { osc, wobble, stagger } from "../../core/AnimationUtils";

/* ═══════════════════════════════════════════════════════════════
   Physics & Engineering — 8 templates
   gear_train, pulley_system, pendulum_swing, magnet_field,
   lens_optics, spring_force, inclined_plane, wave_interference
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Gear Train ──────────────────────────────────────── */
export interface GearTrainProps {
  label?: string;
  /** @deprecated use t instead */
  rotation?: number;
  accentColor?: string;
  /** Seconds since scene start — drives gear rotation (inverse to radius, meshing). */
  t?: number;
}

export const GearTrain: React.FC<GearTrainProps> = ({
  label = "Gear Train",
  accentColor = "#ff9800",
  t = 0,
}) => {
  // Large gear (r=80, 16 teeth) drives medium (r=60) at 80/60 = 1.33× speed,
  // medium drives small (r=45) at 80/45 = 1.78× speed.
  // Adjacent gears mesh in opposite directions.
  const baseAngleDeg = (t / 4) * 360; // one full rotation every 4 s
  const gearTeeth = (n: number, cx: number, cy: number, r: number, toothH: number) =>
    Array.from({ length: n }, (_, i) => {
      const a = (i * 360 / n) * Math.PI / 180;
      return `${cx + Math.cos(a) * (r + toothH)},${cy + Math.sin(a) * (r + toothH)}
              ${cx + Math.cos(a + 0.5 / n * Math.PI) * r},${cy + Math.sin(a + 0.5 / n * Math.PI) * r}`;
    }).join(" ");

  const gears = [
    { cx: 200, cy: 280, r: 80, n: 16, c: accentColor,  dir:  1, ratio: 1.00 },
    { cx: 400, cy: 280, r: 60, n: 12, c: "#3498db",    dir: -1, ratio: 80 / 60 },
    { cx: 550, cy: 280, r: 45, n: 10, c: "#e74c3c",    dir:  1, ratio: 80 / 45 },
  ];

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {gears.map((g, i) => {
        const rot = g.dir * baseAngleDeg * g.ratio;
        return (
          <g key={i} transform={`rotate(${rot}, ${g.cx}, ${g.cy})`}>
            <circle cx={g.cx} cy={g.cy} r={g.r} fill="#1c2833" stroke={g.c} strokeWidth="3" />
            <polygon points={gearTeeth(g.n, g.cx, g.cy, g.r - 5, 15)} fill="none" stroke={g.c} strokeWidth="2" />
            <circle cx={g.cx} cy={g.cy} r="10" fill="#0d1117" stroke={g.c} strokeWidth="2" />
          </g>
        );
      })}
      {/* Speed labels */}
      <text x="200" y="400" textAnchor="middle" fill="#ff9800" fontSize="12">1× speed</text>
      <text x="400" y="380" textAnchor="middle" fill="#3498db" fontSize="12">1.3× speed</text>
      <text x="550" y="365" textAnchor="middle" fill="#e74c3c" fontSize="12">1.8× speed</text>
      <text x="400" y="520" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Pulley System ────────────────────────────────────── */
export interface PulleySystemProps {
  label?: string;
  /** @deprecated use t instead */
  animationProgress?: number;
  /** Seconds since scene start — pulley wheel turns, weight lifts and descends cyclically. */
  t?: number;
}

export const PulleySystem: React.FC<PulleySystemProps> = ({
  label = "Pulley System",
  t = 0,
}) => {
  // Weight oscillates up and down with a 5 s period (sinusoidal).
  const lift = osc(t, 5, 0, 100);
  // Pulley wheel rotates proportional to rope movement; wheel circumference ~188.
  const wheelAngleDeg = (lift / 30) * 360;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Ceiling */}
      <rect x="100" y="60" width="600" height="15" fill="#546e7a" rx="4" />
      {/* Pulley wheel */}
      <g transform={`rotate(${wheelAngleDeg}, 400, 110)`}>
        <circle cx="400" cy="110" r="30" fill="#1c2833" stroke="#bdbdbd" strokeWidth="4" />
        {/* Spoke markers to show rotation */}
        <line x1="400" y1="82" x2="400" y2="138" stroke="#546e7a" strokeWidth="2" />
        <line x1="372" y1="110" x2="428" y2="110" stroke="#546e7a" strokeWidth="2" />
      </g>
      <circle cx="400" cy="110" r="5" fill="#bdbdbd" />
      {/* Rope - left side (effort) */}
      <line x1="370" y1="110" x2="370" y2={450 - lift * 0.5} stroke="#8d6e63" strokeWidth="3" />
      {/* Rope over pulley */}
      <path d="M370,110 Q370,80 400,80 Q430,80 430,110" fill="none" stroke="#8d6e63" strokeWidth="3" />
      {/* Rope - right side (load) */}
      <line x1="430" y1="110" x2="430" y2={400 - lift} stroke="#8d6e63" strokeWidth="3" />
      {/* Weight */}
      <rect x="400" y={400 - lift} width="60" height="50" rx="6" fill="#e74c3c" opacity="0.8" />
      <text x="430" y={430 - lift} textAnchor="middle" fill="#ecf0f1" fontSize="14" fontWeight="600">W</text>
      {/* Effort arrow */}
      <line x1="340" y1={400 - lift * 0.5} x2="340" y2={450 - lift * 0.5} stroke="#4caf50" strokeWidth="3" markerEnd="url(#pl-arr)" />
      <text x="310" y={430 - lift * 0.5} fill="#4caf50" fontSize="12">F</text>
      <defs>
        <marker id="pl-arr" viewBox="0 0 10 10" refX="5" refY="10" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L5,10 L10,0Z" fill="#4caf50" />
        </marker>
      </defs>
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Pendulum Swing ───────────────────────────────────── */
export interface PendulumSwingProps {
  label?: string;
  /** @deprecated use t instead */
  phase?: number;
  /** Seconds since scene start — sinusoidal swing, ~2.5 s period. */
  t?: number;
}

export const PendulumSwing: React.FC<PendulumSwingProps> = ({
  label = "Pendulum",
  t = 0,
}) => {
  // Pendulum period T ≈ 2 π √(L/g); for visual clarity we use 2.5 s.
  // Angle swings ± 35 degrees (sinusoidal, physically correct at small amplitudes).
  const angle = wobble(t, 2.5, 35, 0);
  // Bob glow pulses brightest at ends (max PE) — use |sin| of half the period.
  const glowR = 30 + osc(t, 1.25, 0, 4, Math.PI / 2); // peaks at t=0.625s, 1.875s …
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Support beam */}
      <rect x="300" y="60" width="200" height="12" rx="4" fill="#546e7a" />
      {/* Pendulum (pivot at 400, 72) */}
      <g transform={`rotate(${angle}, 400, 72)`}>
        {/* String */}
        <line x1="400" y1="72" x2="400" y2="370" stroke="#8d6e63" strokeWidth="2" />
        {/* Bob glow */}
        <circle cx="400" cy="390" r={glowR + 6} fill="#e74c3c" opacity="0.15" />
        {/* Bob */}
        <circle cx="400" cy="390" r={glowR} fill="#e74c3c" stroke="#c62828" strokeWidth="2" />
      </g>
      {/* Arc path (static guide) */}
      <path d="M280,390 Q400,420 520,390" fill="none" stroke="#546e7a" strokeWidth="1" strokeDasharray="6,4" />
      {/* Equilibrium line */}
      <line x1="400" y1="72" x2="400" y2="420" stroke="#546e7a" strokeWidth="0.8" strokeDasharray="4,4" />
      {/* Energy labels */}
      <text x="280" y="440" textAnchor="middle" fill="#f9a825" fontSize="12">Max PE</text>
      <text x="400" y="460" textAnchor="middle" fill="#4caf50" fontSize="12">Max KE</text>
      <text x="520" y="440" textAnchor="middle" fill="#f9a825" fontSize="12">Max PE</text>
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Magnet Field ─────────────────────────────────────── */
export interface MagnetFieldProps {
  label?: string;
  /** @deprecated use t instead */
  flowProgress?: number;
  /** Seconds since scene start — field lines flow outward from N, inward to S. */
  t?: number;
}

export const MagnetField: React.FC<MagnetFieldProps> = ({
  label = "Magnetic Field",
  t = 0,
}) => {
  // Dash offset flows continuously; one dash pattern cycle every 1.5 s.
  const offset = -((t % 1.5) / 1.5) * 24;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Bar magnet */}
      <rect x="300" y="260" width="100" height="60" rx="6" fill="#e74c3c" />
      <rect x="400" y="260" width="100" height="60" rx="6" fill="#2196f3" />
      <text x="350" y="297" textAnchor="middle" fill="#fff" fontSize="20" fontWeight="700">N</text>
      <text x="450" y="297" textAnchor="middle" fill="#fff" fontSize="20" fontWeight="700">S</text>
      {/* Field lines — each loop at a different spread; dashoffset drives flow */}
      {[40, 80, 120].map((spreadAmt, i) => (
        <g key={i} opacity={0.4 + i * 0.15}>
          {/* Top field lines */}
          <path
            d={`M350,260 Q350,${260 - spreadAmt} 400,${260 - spreadAmt - 20} Q450,${260 - spreadAmt} 450,260`}
            fill="none" stroke="#7c4dff" strokeWidth="1.5"
            strokeDasharray="8,4" strokeDashoffset={offset - i * 4}
          />
          {/* Bottom field lines */}
          <path
            d={`M350,320 Q350,${320 + spreadAmt} 400,${320 + spreadAmt + 20} Q450,${320 + spreadAmt} 450,320`}
            fill="none" stroke="#7c4dff" strokeWidth="1.5"
            strokeDasharray="8,4" strokeDashoffset={offset - i * 4}
          />
          {/* Left side field lines */}
          <path
            d={`M300,290 Q${300 - spreadAmt},290 ${300 - spreadAmt - 20},${290 - spreadAmt * 0.8}
               Q${300 - spreadAmt},${290 - spreadAmt * 1.6} 350,260`}
            fill="none" stroke="#7c4dff" strokeWidth="1.5"
            strokeDasharray="8,4" strokeDashoffset={offset - i * 4}
          />
          {/* Right side field lines */}
          <path
            d={`M500,290 Q${500 + spreadAmt},290 ${500 + spreadAmt + 20},${290 - spreadAmt * 0.8}
               Q${500 + spreadAmt},${290 - spreadAmt * 1.6} 450,260`}
            fill="none" stroke="#7c4dff" strokeWidth="1.5"
            strokeDasharray="8,4" strokeDashoffset={offset - i * 4}
          />
        </g>
      ))}
      <text x="400" y="500" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Lens Optics ──────────────────────────────────────── */
export interface LensOpticsProps {
  label?: string;
  /** @deprecated use t instead */
  activeIndex?: number;
  /** Seconds since scene start — light rays draw on and lens shimmers. */
  t?: number;
}

export const LensOptics: React.FC<LensOpticsProps> = ({
  label = "Lens & Light",
  t = 0,
}) => {
  // Lens shimmers gently (opacity breathe).
  const lensOpacity = osc(t, 3, 0.15, 0.35);
  // Light ray opacity pulses with a 4 s cycle (staggered per ray).
  const ray1Op = osc(t, 4, 0.5, 1.0, stagger(0, 0.8));
  const ray2Op = osc(t, 4, 0.5, 1.0, stagger(1, 0.8));
  const ray3Op = osc(t, 4, 0.5, 1.0, stagger(2, 0.8));
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Optical axis */}
      <line x1="60" y1="300" x2="740" y2="300" stroke="#546e7a" strokeWidth="1" strokeDasharray="6,4" />
      {/* Convex lens — gently shimmering */}
      <ellipse cx="400" cy="300" rx="15" ry="140" fill="#42a5f5" opacity={lensOpacity} stroke="#42a5f5" strokeWidth="2" />
      {/* Focal points */}
      <circle cx="260" cy="300" r="5" fill="#f9a825" />
      <text x="260" y="325" textAnchor="middle" fill="#f9a825" fontSize="11">F</text>
      <circle cx="540" cy="300" r="5" fill="#f9a825" />
      <text x="540" y="325" textAnchor="middle" fill="#f9a825" fontSize="11">F</text>
      {/* Light rays — pulsing brightness to suggest propagation */}
      <g opacity={ray1Op}>
        {/* Parallel ray → through focal point */}
        <line x1="100" y1="180" x2="400" y2="180" stroke="#f44336" strokeWidth="2" />
        <line x1="400" y1="180" x2="700" y2="380" stroke="#f44336" strokeWidth="2" />
      </g>
      {/* Through center → straight */}
      <line x1="100" y1="400" x2="700" y2="200" stroke="#4caf50" strokeWidth="2" opacity={ray2Op} />
      <g opacity={ray3Op}>
        {/* Through focal point → parallel */}
        <line x1="100" y1="400" x2="400" y2="400" stroke="#2196f3" strokeWidth="2" />
        <line x1="400" y1="400" x2="700" y2="400" stroke="#2196f3" strokeWidth="2" strokeDasharray="6,3" />
      </g>
      {/* Object */}
      <line x1="180" y1="180" x2="180" y2="300" stroke="#ecf0f1" strokeWidth="3" />
      <polygon points="180,180 174,195 186,195" fill="#ecf0f1" />
      <text x="180" y="320" textAnchor="middle" fill="#bdc3c7" fontSize="11">Object</text>
      {/* Image */}
      <line x1="620" y1="300" x2="620" y2="380" stroke="#ecf0f1" strokeWidth="2" strokeDasharray="4,3" />
      <text x="620" y="400" textAnchor="middle" fill="#bdc3c7" fontSize="11">Image</text>
      <text x="400" y="520" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Spring Force ─────────────────────────────────────── */
export interface SpringForceProps {
  label?: string;
  /** @deprecated use t instead */
  phase?: number;
  /** Seconds since scene start — spring oscillates horizontally (SHM, 2 s period). */
  t?: number;
}

export const SpringForce: React.FC<SpringForceProps> = ({
  label = "Spring (Hooke's Law)",
  t = 0,
}) => {
  // Simple Harmonic Motion: x = A sin(2π t / T). Period 2 s, amplitude 60 px.
  const stretch = wobble(t, 2, 60, 0);
  const coils = 8;
  // Restore force arrow direction follows Hooke's law (F = -kx).
  const forceLen = -stretch * 0.5;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Wall */}
      <rect x="100" y="200" width="15" height="160" fill="#546e7a" />
      {Array.from({ length: 6 }, (_, i) => (
        <line key={i} x1="100" y1={210 + i * 28} x2="85" y2={220 + i * 28} stroke="#546e7a" strokeWidth="2" />
      ))}
      {/* Spring coils — stretch changes segment width */}
      <path d={Array.from({ length: coils }, (_, i) => {
        const segW = (280 + stretch) / coils;
        const x = 115 + i * segW;
        return `${i === 0 ? 'M' : 'L'}${x},${260} L${x + segW * 0.25},${240} L${x + segW * 0.75},${320} L${x + segW},${280}`;
      }).join(' ')}
        fill="none" stroke="#4caf50" strokeWidth="3" />
      {/* Mass block */}
      <rect x={395 + stretch} y="250" width="60" height="60" rx="6" fill="#e74c3c" />
      <text x={425 + stretch} y="286" textAnchor="middle" fill="#fff" fontSize="16" fontWeight="600">m</text>
      {/* Restoring force arrow (opposite to displacement — Hooke's law) */}
      {Math.abs(forceLen) > 2 && (
        <line
          x1={460 + stretch} y1="280"
          x2={460 + stretch + forceLen} y2="280"
          stroke="#f9a825" strokeWidth="3" markerEnd="url(#sf-arr)"
        />
      )}
      <defs>
        <marker id="sf-arr" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L10,5 L0,10Z" fill="#f9a825" />
        </marker>
      </defs>
      {/* Equation */}
      <text x="400" y="420" textAnchor="middle" fill="#bdc3c7" fontSize="18" fontFamily="serif" fontStyle="italic">F = -kx</text>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Inclined Plane ───────────────────────────────────── */
export interface InclinedPlaneProps {
  label?: string;
  /** @deprecated use t instead */
  animationProgress?: number;
  /** Seconds since scene start — block slides up then resets, looping every 5 s. */
  t?: number;
}

export const InclinedPlane: React.FC<InclinedPlaneProps> = ({
  label = "Inclined Plane",
  t = 0,
}) => {
  // Block slides up the ramp over 4 s then resets; loop period = 5 s.
  const loopT = t % 5;
  // Ease in-out for the slide portion (0..4 s), then instant reset.
  const rawProg = Math.min(loopT / 4, 1);
  // Smooth the motion: fast at bottom, decelerates at top (easeOut reversed = easeIn).
  const prog = 1 - Math.pow(1 - rawProg, 2);
  const bx = 200 + prog * 380;
  const by = 460 - prog * 260;
  const rampAngle = -Math.atan2(300, 500) * 180 / Math.PI;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Ground */}
      <line x1="100" y1="460" x2="700" y2="460" stroke="#546e7a" strokeWidth="2" />
      {/* Ramp */}
      <polygon points="150,460 650,460 650,160" fill="#1c2833" stroke="#546e7a" strokeWidth="2" />
      {/* Angle arc */}
      <path d="M200,460 Q200,440 215,430" fill="none" stroke="#f9a825" strokeWidth="2" />
      <text x="230" y="455" fill="#f9a825" fontSize="14">θ</text>
      {/* Block sliding up */}
      <g transform={`translate(${bx},${by}) rotate(${rampAngle})`}>
        <rect x="-25" y="-40" width="50" height="40" rx="4" fill="#e74c3c" />
        <text x="0" y="-16" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="600">m</text>
      </g>
      {/* Force equation */}
      <text x="400" y="510" textAnchor="middle" fill="#bdc3c7" fontSize="13">
        mg sin(θ) = friction + ma
      </text>
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Wave Interference ────────────────────────────────── */
export interface WaveInterferenceProps {
  label?: string;
  /** @deprecated use t instead */
  phase?: number;
  /** Seconds since scene start — both waves propagate rightward; resultant shown. */
  t?: number;
}

export const WaveInterference: React.FC<WaveInterferenceProps> = ({
  label = "Wave Interference",
  t = 0,
}) => {
  // Wave phase advances at ~1 cycle per 2 s (angular freq = π rad/s).
  const phaseOffset = (t / 2) * Math.PI * 2;
  // Wave 2 has a fixed phase offset of 1 rad (partial constructive/destructive).
  const wave2Phase = phaseOffset + 1;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Wave 1 */}
      <path d={`M60,180 ${Array.from({ length: 70 }, (_, i) => {
        const x = 60 + i * 10;
        const y = 180 + Math.sin((i * 0.3) + phaseOffset) * 40;
        return `L${x},${y}`;
      }).join(' ')}`} fill="none" stroke="#e74c3c" strokeWidth="2" />
      <text x="40" y="180" fill="#e74c3c" fontSize="12">Wave 1</text>
      {/* Wave 2 */}
      <path d={`M60,340 ${Array.from({ length: 70 }, (_, i) => {
        const x = 60 + i * 10;
        const y = 340 + Math.sin((i * 0.3) + wave2Phase) * 40;
        return `L${x},${y}`;
      }).join(' ')}`} fill="none" stroke="#3498db" strokeWidth="2" />
      <text x="40" y="340" fill="#3498db" fontSize="12">Wave 2</text>
      {/* Resultant (superposition) */}
      <text x="400" y="435" textAnchor="middle" fill="#bdc3c7" fontSize="13">Superposition (Sum)</text>
      <path d={`M60,500 ${Array.from({ length: 70 }, (_, i) => {
        const x = 60 + i * 10;
        const y = 500 + (Math.sin((i * 0.3) + phaseOffset) + Math.sin((i * 0.3) + wave2Phase)) * 30;
        return `L${x},${y}`;
      }).join(' ')}`} fill="none" stroke="#2ecc71" strokeWidth="2.5" />
      {/* Plus sign */}
      <text x="400" y="270" textAnchor="middle" fill="#ecf0f1" fontSize="30" fontWeight="300">+</text>
      {/* Equals sign */}
      <text x="400" y="410" textAnchor="middle" fill="#ecf0f1" fontSize="30" fontWeight="300">=</text>
      <text x="400" y="575" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
